import { isDefined } from '@sgme/fp';
import classnames from 'classnames';
import { ClearIndicator } from 'components/shared/Tags/clear-indicator';
import type { TagOption } from 'components/shared/Tags/model';
import { TagsPicker } from 'components/shared/Tags/tags-picker';
import { WithUnderlyingInfoTooltip } from 'components/shared/UnderlyingInfo/WithUnderlyingInfoTooltip';
import { MenuList } from 'components/shared/underlyings/selector/result/menu-list';
import { NoOptionsMessage } from 'components/shared/underlyings/selector/result/no-option-message';
import { Option } from 'components/shared/underlyings/selector/result/option';
import { UnderlyingTag } from 'components/underlying-tag';
import { useUnderlyingsTags } from 'hooks/underlyings/use-underlyings-tags';
import { useUserNotification } from 'hooks/useUserNotification';
import type { Underlying } from 'models/underlying';
import { HTMLAttributes, useCallback } from 'react';
import { components } from 'react-select';

import { displayUnderlyingCode } from '../../utils';
import type { UnderlyingsFieldProps } from './BatchSingleUnderlyingFieldComponent.connect';

const defaultOnBlur = () => {
  return;
};

function ClearButton({ tabIndex }: Pick<HTMLAttributes<HTMLButtonElement>, 'tabIndex'>) {
  return (
    <button className="btn btn-sm btn-flat-secondary" tabIndex={tabIndex}>
      <i className="icon icon-sm">delete</i>
    </button>
  );
}
const EMPTY_ARRAY = [] as const;

export type BatchSingleUnderlyingFieldProps = UnderlyingsFieldProps & { value: Underlying | undefined };
export function BatchSingleUnderlyingFieldComponent({
  id,
  isClearable = false,
  isDisabled = false,
  onBlur = defaultOnBlur,
  onChange,
  placeholder,
  value,
  tabIndex,
  className,
  tooltipPlacement,
  defaultUnderlyingCodeType,
  children,
  isFocused,
  isCellInactive,
  lostInputBeforeEditing,
  onEditingKeyDown,
  getOptionsFromPastedLabels,
  search,
  onTagChanged,
  onInputChanged,
}: BatchSingleUnderlyingFieldProps) {
  const { warn } = useUserNotification();
  const { validTags, onTagsChanged } = useUnderlyingsTags({
    underlying: value,
    isMulti: false,
    onChange,
    removeInvalidTagsOnUnderlyingsChange: true,
    codeType: defaultUnderlyingCodeType,
    underlyingsSyncMode: 'replace',
  });

  const convertPastedLabelsToTags = useCallback(
    async (pastedLabels: string[]) => {
      const underlyings = await getOptionsFromPastedLabels(pastedLabels, [], defaultUnderlyingCodeType);
      if (underlyings.filter(u => isDefined(u.rawValue)).length > 1) {
        warn({
          title: 'Single underlying',
          content: [
            'The first underlying from the clipboard is pasted, as the current payoff supports one underlying only.',
          ],
        });
        return EMPTY_ARRAY;
      }
      return underlyings;
    },
    [getOptionsFromPastedLabels, defaultUnderlyingCodeType, warn],
  );

  const handleTagsChanged = useCallback(
    (tag: TagOption<Underlying> | undefined) => {
      onTagsChanged(tag ? [tag] : EMPTY_ARRAY, true);
      onTagChanged();
    },
    [onTagsChanged, onTagChanged],
  );

  return (
    <>
      <TagsPicker<Underlying>
        components={{
          MenuList,
          Option,
          NoOptionsMessage,
          SingleValue: props => (
            <components.SingleValue {...props}>
              <WithUnderlyingInfoTooltip underlying={props.data.rawValue} placement={tooltipPlacement}>
                <UnderlyingTag
                  value={props.data.rawValue}
                  label={displayUnderlyingCode(defaultUnderlyingCodeType, props.data.rawValue as Underlying)}
                  isReadOnly
                />
              </WithUnderlyingInfoTooltip>
            </components.SingleValue>
          ),
          ClearIndicator: props => (
            <ClearIndicator.Container {...props}>
              <ClearButton tabIndex={tabIndex} />
            </ClearIndicator.Container>
          ),
        }}
        onInputChanged={onInputChanged}
        convertPastedLabelsToTags={convertPastedLabelsToTags}
        filterTags={input => search(input, [], [], defaultUnderlyingCodeType)}
        getOptionLabel={(option: TagOption<Underlying>) =>
          displayUnderlyingCode(defaultUnderlyingCodeType, option.rawValue as Underlying)
        }
        isMulti={false}
        id={id ?? ''}
        isClearable={isClearable}
        isDisabled={isDisabled}
        onBlur={onBlur}
        onTagsChanged={handleTagsChanged}
        placeholder={placeholder}
        tags={validTags}
        tabIndex={tabIndex}
        btnTabIndex={-1}
        className={classnames(className, {
          disabled: isDisabled,
          'pe-none': isCellInactive,
        })}
        isFocused={isFocused}
        onEditingKeyDown={onEditingKeyDown}
        lostInputBeforeEditing={lostInputBeforeEditing}
      />
      {children}
    </>
  );
}
